import {
	PeriodNavigator,
	type PeriodNavigatorView,
} from "@/shared/components/PeriodNavigator";
import { getMomentTz } from "@dexteel/mesf-core";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
	UTILITY_CONSUMPTION_DIRECTION,
	UTILITY_CONSUMPTION_MODE,
	type UtilityConsumptionDirection,
	type UtilityConsumptionMode,
} from "../models/UtilityConsumption";
import {
	getCurrentUtilityConsumptionPeriod,
	getUtilityConsumptionPeriod,
	getUtilityConsumptionPeriodLabel,
	isUtilityConsumptionNextDisabled,
	moveUtilityConsumptionPeriod,
} from "../utils/calendar";

const moment = getMomentTz();

const MODE_LABELS: Record<UtilityConsumptionMode, string> = {
	day: "Month",
	week: "Week",
	month: "Year",
	date: "Day",
};
const MODE_ORDER: UtilityConsumptionMode[] = [
	UTILITY_CONSUMPTION_MODE.DATE,
	UTILITY_CONSUMPTION_MODE.WEEK,
	UTILITY_CONSUMPTION_MODE.DAY,
	UTILITY_CONSUMPTION_MODE.MONTH,
];

interface ConsumptionControlsProps {
	mode: UtilityConsumptionMode;
	referenceDate: Date;
	plantTimeZone: string;
	onModeChange: (mode: UtilityConsumptionMode) => void;
	onReferenceDateChange: (referenceDate: Date) => void;
}

export const ConsumptionControls = ({
	mode,
	referenceDate,
	plantTimeZone,
	onModeChange,
	onReferenceDateChange,
}: ConsumptionControlsProps) => {
	const period = getUtilityConsumptionPeriod(
		mode,
		referenceDate,
		plantTimeZone,
	);

	const views: PeriodNavigatorView[] =
		mode === UTILITY_CONSUMPTION_MODE.MONTH
			? ["year"]
			: mode === UTILITY_CONSUMPTION_MODE.DAY
				? ["year", "month"]
				: ["year", "month", "day"];

	const movePeriod = (direction: UtilityConsumptionDirection) =>
		onReferenceDateChange(
			moveUtilityConsumptionPeriod(
				mode,
				referenceDate,
				direction,
				plantTimeZone,
			),
		);

	return (
		<Box
			sx={{
				display: "flex",
				flexWrap: "wrap",
				gap: 1,
				alignItems: "center",
			}}
		>
			<ToggleButtonGroup
				exclusive
				size="small"
				value={mode}
				aria-label="Consumption period mode"
				onChange={(_, value: UtilityConsumptionMode | null) =>
					value && onModeChange(value)
				}
			>
				{MODE_ORDER.map((value) => (
					<ToggleButton
						key={value}
						value={value}
						aria-label={`${MODE_LABELS[value]} mode`}
					>
						{MODE_LABELS[value]}
					</ToggleButton>
				))}
			</ToggleButtonGroup>
			<PeriodNavigator
				label={getUtilityConsumptionPeriodLabel(
					mode,
					referenceDate,
					plantTimeZone,
				)}
				value={moment.utc(referenceDate).tz(plantTimeZone)}
				views={views}
				nextDisabled={isUtilityConsumptionNextDisabled(period, plantTimeZone)}
				onPrevious={() => movePeriod(UTILITY_CONSUMPTION_DIRECTION.PREVIOUS)}
				onNext={() => movePeriod(UTILITY_CONSUMPTION_DIRECTION.NEXT)}
				onCurrent={() =>
					onReferenceDateChange(
						getCurrentUtilityConsumptionPeriod(mode, plantTimeZone).startUtc,
					)
				}
				onChange={onReferenceDateChange}
			/>
		</Box>
	);
};
